import { useState } from "react";
import {
  Thermometer, Plus, Check, AlertTriangle, Snowflake, Flame
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const TemperaturTab = ({ temperatureLogs, onAdd }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [newTemp, setNewTemp] = useState({ location: "", temperature: "", notes: "" });

  const handleSubmit = () => {
    if (!newTemp.location || !newTemp.temperature) return;
    onAdd(newTemp);
    setShowDialog(false);
    setNewTemp({ location: "", temperature: "", notes: "" });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white">Temperaturprotokoll</h2>
        <Button onClick={() => setShowDialog(true)} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="add-temp-btn">
          <Plus size={16} /> Temperatur erfassen
        </Button>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-blue-400 mb-1">
            <Snowflake size={16} /><span className="text-xs font-medium">Tiefkuehlung</span>
          </div>
          <p className="text-lg font-bold text-blue-300">-18 Grad C</p>
          <p className="text-xs text-blue-500">oder kaelter</p>
        </div>
        <div className="bg-cyan-900/20 border border-cyan-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-cyan-400 mb-1">
            <Thermometer size={16} /><span className="text-xs font-medium">Kuehlung</span>
          </div>
          <p className="text-lg font-bold text-cyan-300">2-7 Grad C</p>
          <p className="text-xs text-cyan-500">optimal</p>
        </div>
        <div className="bg-orange-900/20 border border-orange-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-orange-400 mb-1">
            <Flame size={16} /><span className="text-xs font-medium">Warmhalten</span>
          </div>
          <p className="text-lg font-bold text-orange-300">&ge;65 Grad C</p>
          <p className="text-xs text-orange-500">mind.</p>
        </div>
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400 mb-1">
            <AlertTriangle size={16} /><span className="text-xs font-medium">Gefahrenzone</span>
          </div>
          <p className="text-lg font-bold text-red-300">7-60 Grad C</p>
          <p className="text-xs text-red-500">vermeiden!</p>
        </div>
      </div>

      {temperatureLogs.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
          <Thermometer size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">Keine Temperaturprotokolle vorhanden</p>
          <p className="text-xs text-slate-500 mt-2">Dokumentieren Sie taeglich die Kuehlschrank- und Tiefkuehltemperaturen</p>
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300">Datum/Zeit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300">Standort</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300">Temperatur</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-300">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {temperatureLogs.slice(0, 20).map((log) => (
                <tr key={log.id} className="hover:bg-slate-700/50">
                  <td className="px-4 py-3 text-sm text-slate-400">
                    {new Date(log.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-white">{log.location}</td>
                  <td className="px-4 py-3 text-sm font-mono font-bold text-white">{log.temperature} Grad C</td>
                  <td className="px-4 py-3">
                    {log.isOk ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-900/30 text-green-400">
                        <Check size={12} /> OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-900/30 text-red-400">
                        <AlertTriangle size={12} /> Pruefen!
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Thermometer className="text-blue-400" /> Temperatur erfassen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Standort</Label>
              <Select value={newTemp.location} onValueChange={(v) => setNewTemp({ ...newTemp, location: v })}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Waehlen..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Kuehlschrank 1">Kuehlschrank 1</SelectItem>
                  <SelectItem value="Kuehlschrank 2">Kuehlschrank 2</SelectItem>
                  <SelectItem value="Tiefkuehler">Tiefkuehler</SelectItem>
                  <SelectItem value="Getraenkekuehlschrank">Getraenkekuehlschrank</SelectItem>
                  <SelectItem value="Warmhaltung">Warmhaltung</SelectItem>
                  <SelectItem value="Sonstige">Sonstige</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Temperatur (Grad C)</Label>
              <Input type="number" step="0.1" placeholder="-18" value={newTemp.temperature}
                onChange={(e) => setNewTemp({ ...newTemp, temperature: e.target.value })}
                className="bg-slate-700 border-slate-600" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Anmerkungen (optional)</Label>
              <Input placeholder="z.B. Nach Lieferung" value={newTemp.notes}
                onChange={(e) => setNewTemp({ ...newTemp, notes: e.target.value })}
                className="bg-slate-700 border-slate-600" />
            </div>
            <Button onClick={handleSubmit} className="w-full bg-blue-600 hover:bg-blue-700">Temperatur speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TemperaturTab;
